import * as React from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { LocateFixed, LocateOff, Menu, RefreshCw, Search } from 'lucide-react'
import type { CustomerSegment, CustomerStatus } from '@acs/shared'
import { RegionMark } from '@/components/shell/region-plate'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'
import { useRelativeTime } from '@/hooks/use-relative-time'
import type { RealtimeStatus } from '@/hooks/use-realtime'
import { useNavCounts } from '@/hooks/use-stats'
import { useUiStore } from '@/stores/ui-store'
import { cn } from '@/lib/cn'

/* ------------------------------------------------------------------ *
 * Connection state
 *
 * Lives here because the topbar owns the pill, but the rail's dot reads the
 * same descriptor — one derivation, so the two indicators can never disagree
 * about whether the stream is up.
 *
 * The colours are semantic and never the accent: indigo means "you selected
 * this", and a connection light that borrowed it would be claiming to be a
 * selection. Green up, amber recovering, red down.
 * ------------------------------------------------------------------ */

export type ConnectionLevel = 'live' | 'reconnecting' | 'down'

export interface ConnectionMeta {
  level: ConnectionLevel
  /** Short label for the pill. */
  label: string
  /** Full sentence for the tooltip, including the last-event age. */
  tooltip: string
  /** Text colour token. The dot inherits it via `currentColor`. */
  colorClass: string
  /** Soft tint behind the pill. Borderless — the tint is the whole signal. */
  pillClass: string
  /** Only a genuinely healthy stream is allowed to animate. */
  pulse: boolean
}

/**
 * Backoff doubles from 1s, so by the fourth attempt the stream has been down
 * for roughly fifteen seconds — long enough that "reconnecting" stops being
 * reassuring and starts being a lie.
 */
const DOWN_AFTER_ATTEMPTS = 4

export function useConnectionMeta(realtime: RealtimeStatus): ConnectionMeta {
  const lastEventIso = React.useMemo(
    () => (realtime.lastEventAt === null ? null : new Date(realtime.lastEventAt).toISOString()),
    [realtime.lastEventAt]
  )
  const lastEventAgo = useRelativeTime(lastEventIso)

  return React.useMemo<ConnectionMeta>(() => {
    const seen = lastEventIso === null ? 'no updates yet' : `last update ${lastEventAgo}`

    if (realtime.connected) {
      return {
        level: 'live',
        label: 'Live',
        tooltip: `Updating in real time · ${seen}`,
        colorClass: 'text-live',
        pillClass: 'bg-live-soft',
        pulse: true,
      }
    }

    if (realtime.reconnectAttempt >= DOWN_AFTER_ATTEMPTS) {
      return {
        level: 'down',
        label: 'Offline',
        tooltip: `Not connected · ${seen}. You are looking at the last data we received.`,
        colorClass: 'text-danger',
        pillClass: 'bg-danger-soft',
        pulse: false,
      }
    }

    return {
      level: 'reconnecting',
      label: realtime.reconnectAttempt === 0 ? 'Connecting' : 'Reconnecting',
      tooltip:
        realtime.reconnectAttempt === 0
          ? `Connecting to live updates · ${seen}`
          : `Reconnecting to live updates · ${seen}`,
      colorClass: 'text-warn',
      pillClass: 'bg-warn-soft',
      pulse: false,
    }
  }, [realtime.connected, realtime.reconnectAttempt, lastEventIso, lastEventAgo])
}

/** The dot alone — shape and colour, no text. Used where space is tight. */
export function ConnectionDot({ meta, className }: { meta: ConnectionMeta; className?: string }) {
  return (
    <span className={cn('inline-flex items-center', meta.colorClass, className)}>
      {meta.pulse ? (
        <span className="pulse-dot" aria-hidden="true" />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            'size-[7px] flex-none rounded-full',
            meta.level === 'down' ? 'bg-current' : 'border-2 border-current bg-transparent'
          )}
        />
      )}
      <span className="sr-only">{meta.tooltip}</span>
    </span>
  )
}

export function ConnectionPill({ meta }: { meta: ConnectionMeta }) {
  return (
    <Tooltip content={meta.tooltip} side="bottom">
      <span
        className={cn(
          'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5',
          'text-[12px] font-medium tracking-[0.04em] uppercase',
          meta.pillClass,
          meta.colorClass
        )}
        role="status"
        aria-live="polite"
      >
        {meta.pulse ? (
          <span className="pulse-dot" aria-hidden="true" />
        ) : (
          <span
            aria-hidden="true"
            className={cn(
              'size-[7px] flex-none rounded-full',
              meta.level === 'down' ? 'bg-current' : 'border-2 border-current'
            )}
          />
        )}
        {meta.label}
      </span>
    </Tooltip>
  )
}

/* ------------------------------------------------------------------ *
 * Page meta
 *
 * The topbar shows "how many rows am I looking at". For the customer lists
 * that number is already in `useStats()`. For everything else the route is the
 * only thing that knows, so it publishes upward through this context rather
 * than the topbar reaching into another view's query cache.
 * ------------------------------------------------------------------ */

interface PageMetaApi {
  count: number | null
  setCount: (count: number | null) => void
}

const PageMetaContext = React.createContext<PageMetaApi | null>(null)

export function PageMetaProvider(p: { children: React.ReactNode }) {
  const [count, setCount] = React.useState<number | null>(null)
  const value = React.useMemo<PageMetaApi>(() => ({ count, setCount }), [count])
  return <PageMetaContext.Provider value={value}>{p.children}</PageMetaContext.Provider>
}

/**
 * Publish the current view's row count to the topbar. Clears on unmount so a
 * stale number never outlives the view that produced it.
 */
export function useSetPageCount(count: number | null): void {
  const ctx = React.useContext(PageMetaContext)
  const setCount = ctx?.setCount

  React.useEffect(() => {
    if (!setCount) return
    setCount(count)
    return () => {
      setCount(null)
    }
  }, [setCount, count])
}

/* ------------------------------------------------------------------ *
 * Title
 * ------------------------------------------------------------------ */

const STATUS_TITLES: Record<CustomerStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  confirmed: 'Confirmed',
}

interface PageDescriptor {
  title: string
  /** Second line / suffix, e.g. the session id on a deep link. */
  detail: string | null
  status: CustomerStatus | null
  /** Set when the view is a segmented agent queue rather than a plain status. */
  segment: CustomerSegment | null
  noun: string
}

function describe(pathname: string): PageDescriptor {
  const segments = pathname.split('/').filter(Boolean)
  const head = segments[0]

  if (head === 'customers') {
    const raw = segments[1]
    /*
     * The segmented queues, BEFORE the status coercion below.
     *
     * That coercion maps anything it does not recognise to `approved`, so
     * without this the Telegram list would title itself "Approved" and show the
     * approved count — a wrong number next to a right one, on the same screen
     * as a table that disagrees with it.
     *
     * `status` stays `pending` because that is what the view really is; only
     * the count and the title come from the segment.
     */
    if (raw === 'telegram') {
      return {
        title: 'Telegram',
        detail: null,
        status: 'pending',
        segment: 'telegram',
        noun: 'customers',
      }
    }
    const status: CustomerStatus =
      raw === 'pending' || raw === 'approved' || raw === 'confirmed' ? raw : 'approved'
    return { title: STATUS_TITLES[status], detail: null, status, segment: null, noun: 'customers' }
  }

  if (head === 'schedules') {
    return { title: 'Schedules', detail: null, status: null, segment: null, noun: 'schedules' }
  }

  if (head === 'sessions') {
    return {
      title: 'Session',
      detail: segments[1] ?? null,
      status: null,
      segment: null,
      noun: 'customers',
    }
  }

  return { title: 'Console', detail: null, status: null, segment: null, noun: 'rows' }
}

const COUNT_FORMAT = new Intl.NumberFormat('en-US')

/* ------------------------------------------------------------------ *
 * Auto-scroll switch
 *
 * Whether the customer table scrolls to a row the moment it goes ACTIVE —
 * `readInitialAutoScrollHits` has the why. It sits in the topbar, not the
 * rail, because it is reached for mid-wave, the moment the table starts
 * swinging, and the topbar is right above the table at every width.
 *
 * The accessible name is fixed and `aria-pressed` carries on/off, so a screen
 * reader announces one toggle rather than a label that rewrites itself; the
 * tooltip spells out what each state does.
 * ------------------------------------------------------------------ */

function AutoScrollToggle() {
  const on = useUiStore((s) => s.autoScrollHits)
  const toggle = useUiStore((s) => s.toggleAutoScrollHits)

  const hint = on
    ? 'Auto-scroll on: the table jumps to each customer as a shift opens for them. Click to keep it still.'
    : 'Auto-scroll off: the table stays where you put it. Rows still light up when a shift opens.'

  return (
    <Tooltip content={hint} side="bottom">
      <Button
        variant="secondary"
        size="md"
        aria-label="Auto-scroll to new hits"
        aria-pressed={on}
        onClick={toggle}
        className={cn(
          // Icon-only below `sm`, like Search beside it.
          'w-9 px-0 sm:w-auto sm:px-3',
          on
            ? 'border-accent/40 bg-accent-soft text-accent hover:border-accent hover:bg-accent-soft'
            : 'text-muted hover:text-fg'
        )}
      >
        {on ? <LocateFixed aria-hidden="true" /> : <LocateOff aria-hidden="true" />}
        <span className="hidden sm:inline">Auto-scroll</span>
      </Button>
    </Tooltip>
  )
}

/* ------------------------------------------------------------------ *
 * Topbar
 * ------------------------------------------------------------------ */

/**
 * Sixty pixels holding four things: where am I, how many, find something, is
 * the data fresh — plus, on the customer lists, whether the table follows new
 * hits. Below `md` the hamburger joins them, and the auto-scroll switch and the
 * search pill contract to their icons, because a 360px bar cannot hold a word and a
 * keyboard hint that no phone has a keyboard for anyway.
 */
export function Topbar({ realtime, className }: { realtime: RealtimeStatus; className?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const page = React.useMemo(() => describe(pathname), [pathname])

  const meta = useConnectionMeta(realtime)
  const setCommandOpen = useUiStore((s) => s.setCommandOpen)
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen)
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen)

  const nav = useNavCounts()
  const ctx = React.useContext(PageMetaContext)

  /*
   * Customer lists get their count from the aggregate the header already
   * fetches; every other view publishes its own.
   *
   * Read through `useNavCounts` rather than `stats.customers` directly, so that
   * "Pending" here means exactly what the rail's Pending badge means — net of
   * the segmented queues — and matches the table's own footer total, which the
   * server computed with the same exclusion.
   */
  const count: number | null =
    page.segment !== null
      ? (nav.segments?.[page.segment] ?? null)
      : page.status !== null
        ? (nav.customers?.[page.status] ?? null)
        : (ctx?.count ?? null)

  const queryClient = useQueryClient()
  const fetching = useIsFetching()
  const [refreshing, setRefreshing] = React.useState(false)

  const refresh = React.useCallback(() => {
    setRefreshing(true)
    void queryClient.invalidateQueries().finally(() => {
      setRefreshing(false)
    })
  }, [queryClient])

  const openCommand = React.useCallback(() => {
    setCommandOpen(true)
  }, [setCommandOpen])

  const spinning = refreshing || fetching > 0

  return (
    <header
      className={cn(
        'flex h-topbar min-w-0 flex-none items-center gap-2 border-b border-line bg-s1 md:gap-3',
        'pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]',
        'md:pl-[max(1rem,env(safe-area-inset-left))] md:pr-[max(1rem,env(safe-area-inset-right))]',
        className
      )}
    >
      <Button
        variant="ghost"
        size="md"
        iconOnly
        className="md:hidden"
        aria-label="Open navigation"
        aria-expanded={mobileNavOpen}
        onClick={() => {
          setMobileNavOpen(true)
        }}
      >
        <Menu aria-hidden="true" />
      </Button>

      {/* Below `md` the rail is gone, and with it the flag in its corner — and a
          phone with two consoles in two tabs is exactly where the flag is
          needed. Same mark, smaller, beside the page title. */}
      <span className="flex flex-none items-center md:hidden">
        <RegionMark size={22} />
        <span className="sr-only">Canada console</span>
      </span>

      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <h1 className="truncate font-display text-[16px] font-semibold tracking-[-0.012em] text-fg">
          {page.title}
        </h1>

        {page.detail !== null ? (
          <span className="hidden truncate font-mono text-[12.5px] text-muted sm:inline">
            {page.detail}
          </span>
        ) : count !== null ? (
          <span className="flex-none text-[13px] text-muted tabular">
            {COUNT_FORMAT.format(count)}
            <span className="ml-1 hidden sm:inline">{page.noun}</span>
          </span>
        ) : (
          <span className="h-3 w-14 flex-none rounded-xs shimmer" aria-hidden="true" />
        )}
      </div>

      <div className="flex flex-none items-center gap-1 md:gap-2">
        {/* Customer lists only: no other view scrolls to a hit. */}
        {page.status !== null ? <AutoScrollToggle /> : null}

        <Button
          variant="ghost"
          size="md"
          iconOnly
          className="sm:hidden"
          aria-label="Search"
          onClick={openCommand}
        >
          <Search aria-hidden="true" />
        </Button>

        <button
          type="button"
          onClick={openCommand}
          className={cn(
            'hidden h-9 items-center gap-2 rounded-md border border-line bg-s1 pr-1.5 pl-3 sm:inline-flex',
            'text-[13.5px] text-muted transition-colors duration-100',
            'hover:bg-s3 hover:text-fg'
          )}
        >
          <Search aria-hidden="true" className="size-4" />
          <span>Search</span>
          <Kbd>Ctrl K</Kbd>
        </button>

        <Tooltip content="Refresh" side="bottom">
          <Button
            variant="ghost"
            size="md"
            iconOnly
            aria-label="Refresh all data"
            onClick={refresh}
            disabled={spinning}
          >
            <RefreshCw aria-hidden="true" className={cn(spinning && 'animate-spin')} />
          </Button>
        </Tooltip>

        {/* The pill needs room for a word; below `sm` the dot says the same
            thing in 7px. */}
        <span className="hidden sm:inline-flex">
          <ConnectionPill meta={meta} />
        </span>
        <Tooltip content={meta.tooltip} side="bottom">
          <span className="flex size-9 items-center justify-center sm:hidden">
            <ConnectionDot meta={meta} />
          </span>
        </Tooltip>
      </div>
    </header>
  )
}
